import { Bidder, bidDecisionSeed } from "@ravalmatic/bridge-engine/ai/bidder";
import { Difficulty } from "@ravalmatic/bridge-engine/ai/difficulty";
import { InferencePolicyModel, PolicyManifest } from "@ravalmatic/bridge-engine/ai/model-inference";
import {
	AuctionState,
	Call,
	Contract,
	Deal,
	PASS,
	Seat,
	applyCall,
	auctionState,
	finalContract,
	legalCalls
} from "@ravalmatic/bridge-engine/core";
import { GameConstants } from "../../GameConstants";
import { GameEvent } from "@ravalmatic/catafractum-utils";
import { GameVars } from "../../GameVars";
import { GameEventType } from "../../Types";
import { ActionScheduler } from "./ActionScheduler";
import { LOCAL_PLAYER_SEAT, getPlayerIdentity } from "./PlayerSeats";

export type BiddingCall = {
	seat: Seat;
	call: Call;
};

export type BiddingSnapshot = {
	state: AuctionState;
	calls: readonly BiddingCall[];
	legalCalls: readonly Call[];
	selectedCall: Call | null;
	isPlayerTurn: boolean;
	thinkingSeat: Seat | null;
	contract: Contract | null;
	isForcedFinish: boolean;
};

type BiddingListener = (snapshot: BiddingSnapshot) => void;

export class BiddingManager {

	public static readonly PLAYER_SEAT: Seat = LOCAL_PLAYER_SEAT;

	private static readonly BOT_THINKING_TIME = 2750;
	private static bidderPromise?: Promise<Bidder>;

	private readonly deal: Deal;
	private readonly seed: string;
	private readonly boardNumber: number;
	private readonly listeners = new Set<BiddingListener>();
	private readonly actionScheduler = new ActionScheduler();
	private calls: Call[];
	private thinkingSeat: Seat | null = null;
	private running = false;
	private isForcedFinish = false;

	constructor(deal: Deal, seed: string, boardNumber: number, calls: readonly Call[] = []) {

		this.deal = deal;
		this.seed = seed;
		this.boardNumber = boardNumber;
		this.calls = [...calls];
	}

	public start(): void {

		if (this.running || this.getState().isOver) {
			return;
		}

		this.running = true;
		this.advanceTurn();
	}

	public stop(): void {

		if (!this.running && this.thinkingSeat === null) {
			return;
		}

		this.running = false;
		this.thinkingSeat = null;
		this.actionScheduler.cancel();
		this.emitState();
	}

	public subscribe(listener: BiddingListener): () => void {

		this.listeners.add(listener);
		listener(this.getSnapshot());
		return () => this.listeners.delete(listener);
	}

	public submitPlayerCall(call: Call): void {

		const state = this.getState();
		if (!this.running || state.isOver || state.turn !== BiddingManager.PLAYER_SEAT) {
			return;
		}

		this.applyCall(call);
	}

	public finish(): void {

		if (this.getState().isOver) {
			return;
		}

		this.isForcedFinish = true;
		while (!this.getState().isOver) {
			this.calls = applyCall(this.deal.dealer, this.calls, PASS);
		}

		this.finishAuction();
	}

	public getState(): AuctionState {

		return auctionState(this.deal.dealer, [...this.calls]);
	}

	public getCalls(): readonly Call[] {

		return [...this.calls];
	}

	public getCurrentSeat(): Seat {

		return this.getState().turn;
	}

	public getLegalCalls(state = this.getState()): Call[] {

		return legalCalls(state)
			.map((isLegal, call) => isLegal ? call as Call : null)
			.filter((call): call is Call => call !== null);
	}

	public getFinalContract(): Contract | null {

		return finalContract(this.deal.dealer, this.calls);
	}

	public getSnapshot(): BiddingSnapshot {

		const state = this.getState();
		return {
			state,
			calls: this.calls.map((call, index) => ({
				seat: ((this.deal.dealer + index) % 4) as Seat,
				call
			})),
			legalCalls: this.getLegalCalls(state),
			selectedCall: state.lastBid,
			isPlayerTurn: this.running && !state.isOver && state.turn === BiddingManager.PLAYER_SEAT,
			thinkingSeat: this.thinkingSeat,
			contract: state.isOver ? this.getFinalContract() : null,
			isForcedFinish: this.isForcedFinish
		};
	}

	private applyCall(call: Call): void {

		this.calls = applyCall(this.deal.dealer, this.calls, call);
		this.thinkingSeat = null;

		if (this.getState().isOver) {
			this.finishAuction();
			return;
		}

		this.advanceTurn();
	}

	private finishAuction(): void {

		this.running = false;
		this.thinkingSeat = null;
		this.actionScheduler.cancel();
		const snapshot = this.emitState();
		GameVars.eventsDispatcher.dispatchEvent(new GameEvent(
			GameEventType.BIDDING_FINISHED,
			snapshot
		));
	}

	private advanceTurn(): void {

		const state = this.getState();
		if (!this.running || state.isOver) {
			return;
		}

		this.thinkingSeat = state.turn === BiddingManager.PLAYER_SEAT ? null : state.turn;
		this.actionScheduler.cancel();
		this.emitState();
		if (this.running && this.thinkingSeat === state.turn && this.getCurrentSeat() === state.turn) {
			this.actionScheduler.schedule(BiddingManager.BOT_THINKING_TIME, async isCurrent => {
				const bidder = await BiddingManager.getBidder();
				if (!isCurrent() || !this.running || this.getCurrentSeat() !== state.turn) {
					return;
				}
				const call = bidder.chooseCall(this.deal, [...this.calls], {
					difficulty: this.getBotDifficulty(state.turn),
					seed: bidDecisionSeed(this.seed, this.boardNumber, this.calls.length)
				});
				this.applyCall(call);
			}, error => {
				this.running = false;
				this.thinkingSeat = null;
				console.error("Unable to choose bot call", error);
				this.emitState();
			});
		}
	}

	private getBotDifficulty(seat: Seat): Difficulty {

		const skill = GameConstants.BOT_PROFILES.find(profile => profile.id === getPlayerIdentity(seat).profileId)?.skill ?? 1;
		if (skill < .5) {
			return "beginner";
		}
		if (skill < .8) {
			return "intermediate";
		}
		return "expert";
	}

	private emitState(): BiddingSnapshot {

		const snapshot = this.getSnapshot();
		this.listeners.forEach(listener => listener(snapshot));
		return snapshot;
	}

	private static getBidder(): Promise<Bidder> {

		if (!BiddingManager.bidderPromise) {
			BiddingManager.bidderPromise = Promise.all([
				fetch("assets/models/sl.json").then(response => {
					if (!response.ok) {
						throw new Error(`Unable to load bidding model manifest: ${response.status}`);
					}
					return response.json() as Promise<PolicyManifest>;
				}),
				fetch("assets/models/sl.bin").then(response => {
					if (!response.ok) {
						throw new Error(`Unable to load bidding model weights: ${response.status}`);
					}
					return response.arrayBuffer();
				})
			]).then(([manifest, buffer]) => new Bidder(
				new InferencePolicyModel(manifest, new Float32Array(buffer))
			));
		}

		return BiddingManager.bidderPromise;
	}
}
